import { styled } from "styled-components";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { colors } from "../../../theme/colors";
import { Card } from "../../../components/ui/card/card.component";
import { Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { BusinessProfile } from "../../../models/business-profile.model";
import { axios } from "../../../api/axios";
import { AxiosError } from "axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import useModal from "antd/es/modal/useModal";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { Center } from "../../../components/layout/center/center.component";
import { valueToLabel } from "../../../utils/functions/misc/valueToLabel";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../atoms/masters.atom";
import { Pressable } from "../../../components/ui/pressable/pressable.component";
import { FacebookIcon, LinkedinIcon, WhatsappIcon, XIcon } from "react-share";
import { MdEmail, MdPhone, MdPublic, MdShare } from "react-icons/md";
import toast, { Toaster } from "react-hot-toast";
import { authAtom } from "../../../atoms/auth.atom";

export const ViewBusinessProfile = () => {
  const auth = useAtomValue(authAtom);

  const { customURL } = useParams();

  const [modal, modalConfig] = useModal();

  const [searchParams] = useSearchParams();

  const master = useAtomValue(masterAtom);

  const navigate = useNavigate();

  const logoRef = useRef<HTMLDivElement | null>(null);

  const [businessProfile, setBusinessProfile] =
    useState<BusinessProfile | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (auth.loaded && !customURL) {
      axios
        .get<{ profile: BusinessProfile }>("/auth/business/profile")
        .then((result) => {
          setLoaded((_) => true);
          setBusinessProfile(result.data.profile);
        })
        .catch((err) => {
          if (err instanceof AxiosError && err.response?.status === 404) {
            setLoaded((_) => true);
          } else {
            handleResponse(err);
          }
        });
    } else if (customURL) {
      axios
        .get<BusinessProfile>("/client/profile/business", {
          headers: { "X-Profile-Id": customURL },
        })
        .then((result) => {
          setLoaded((_) => true);
          setBusinessProfile(result.data);
        })
        .catch((err) => {
          if (err instanceof AxiosError && err.response?.status === 404) {
            setLoaded((_) => true);
          } else {
            handleResponse(err);
          }
        });
    } else {
      navigate("/");
    }
  }, [auth, customURL]);

  useEffect(() => {
    if (loaded && !businessProfile) {
      modal.confirm({
        title: "Would you like to create a business profile?",
        content:
          "You have not yet created a business profile. Would you like to create one?",
        okText: "Create",
        onOk: () => {
          navigate(
            "/b/create" +
              (searchParams.get("r") ? `?r=${searchParams.get("r")}` : ""),
            { replace: true }
          );
        },
        onCancel: () => {
          navigate("/");
        },
      });
    }
  }, [loaded, businessProfile]);

  const onShare = async () => {
    if (!businessProfile) return;
    const url = `https://distribute.pocketfilms.in/${businessProfile.custom_username}`;

    if (
      (window.navigator as any).share &&
      window.navigator.canShare({ url })
    ) {
      try {
        await navigator.share({ url });
      } catch (e) {}
    } else {
      toast.success("Copied to clipboard!");
      window.navigator.clipboard.writeText(url);
    }
  };

  const subtitleParts = businessProfile
    ? [
        businessProfile.doing_business_as &&
        businessProfile.doing_business_as !== businessProfile.business_name
          ? `Doing business as ${businessProfile.doing_business_as}`
          : null,
        valueToLabel("company_types", businessProfile.company_type, master),
        valueToLabel("business", businessProfile.primary_activity, master),
      ].filter(Boolean)
    : [];

  const otherActivities = (businessProfile?.other_activities ?? [])
    .map((activity) => valueToLabel("business", activity, master))
    .filter(Boolean) as string[];

  return (
    <Page>
      {modalConfig}
      {customURL ? (
        <Toaster
          toastOptions={{
            style: {
              fontSize: "1.3rem",
            },
          }}
        />
      ) : null}
      <BackButton />

      {loaded && businessProfile ? (
        <Container>
          <HeaderCard>
            <HeaderTop>
              <Logo
                ref={logoRef}
                style={{
                  backgroundImage: `url(${
                    businessProfile.logo ?? "/images/ui/company.png"
                  })`,
                }}
                onError={() => {
                  if (logoRef.current)
                    logoRef.current.style.backgroundImage =
                      "url(/images/ui/company.png)";
                }}
              />
              <HeaderInfo>
                <Name>{businessProfile.business_name}</Name>
                {subtitleParts.length > 0 ? (
                  <Subtitle>{subtitleParts.join(" · ")}</Subtitle>
                ) : null}
              </HeaderInfo>
              <HeaderActions>
                {!customURL ? (
                  <ActionButton onClick={() => navigate("/b/edit")}>
                    <EditOutlined />
                    <span>Edit</span>
                  </ActionButton>
                ) : null}
                <ActionButton $primary onClick={onShare}>
                  <MdShare size={16} />
                  <span>Share</span>
                </ActionButton>
              </HeaderActions>
            </HeaderTop>
            {businessProfile.about ? (
              <About>{businessProfile.about}</About>
            ) : null}
          </HeaderCard>

          <CardGrid>
            <SectionCard>
              <SectionHeader>
                <SectionTitle>Contact</SectionTitle>
              </SectionHeader>
              <List>
                {businessProfile.business_email ? (
                  <ListRow>
                    <IconCircle>
                      <MdEmail size={16} />
                    </IconCircle>
                    <ListText>
                      <ListLabel>Email</ListLabel>
                      <LinkText
                        onClick={() =>
                          window.open(
                            `mailto:${businessProfile.business_email}`
                          )
                        }
                      >
                        {businessProfile.business_email}
                      </LinkText>
                    </ListText>
                  </ListRow>
                ) : null}
                {businessProfile.primary_phone_code &&
                businessProfile.primary_phone_number ? (
                  <ListRow>
                    <IconCircle>
                      <MdPhone size={16} />
                    </IconCircle>
                    <ListText>
                      <ListLabel>Primary Phone</ListLabel>
                      <LinkText
                        onClick={() =>
                          window.open(
                            `tel:+${businessProfile.primary_phone_code}${businessProfile.primary_phone_number}`
                          )
                        }
                      >
                        +{businessProfile.primary_phone_code}
                        {businessProfile.primary_phone_number}
                      </LinkText>
                    </ListText>
                  </ListRow>
                ) : null}
                {businessProfile.secondary_phone_code &&
                businessProfile.secondary_phone_number ? (
                  <ListRow>
                    <IconCircle>
                      <MdPhone size={16} />
                    </IconCircle>
                    <ListText>
                      <ListLabel>Mobile</ListLabel>
                      <LinkText
                        onClick={() =>
                          window.open(
                            `tel:+${businessProfile.secondary_phone_code}${businessProfile.secondary_phone_number}`
                          )
                        }
                      >
                        +{businessProfile.secondary_phone_code}
                        {businessProfile.secondary_phone_number}
                      </LinkText>
                    </ListText>
                  </ListRow>
                ) : null}
                {businessProfile.website_url ? (
                  <ListRow>
                    <IconCircle>
                      <MdPublic size={16} />
                    </IconCircle>
                    <ListText>
                      <ListLabel>Website</ListLabel>
                      <LinkText
                        onClick={() =>
                          window.open(businessProfile.website_url, "_blank")
                        }
                      >
                        {businessProfile.website_url.replace(
                          /^https?:\/\//,
                          ""
                        )}
                      </LinkText>
                    </ListText>
                  </ListRow>
                ) : null}
                {!businessProfile.business_email &&
                !(
                  businessProfile.primary_phone_code &&
                  businessProfile.primary_phone_number
                ) &&
                !businessProfile.website_url ? (
                  <Empty>No contact details added.</Empty>
                ) : null}
                <SocialRow>
                  {businessProfile.facebook_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(businessProfile.facebook_url, "_blank")
                      }
                    >
                      <FacebookIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {businessProfile.linkedin_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(businessProfile.linkedin_url, "_blank")
                      }
                    >
                      <LinkedinIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {businessProfile.x_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(businessProfile.x_url, "_blank")
                      }
                    >
                      <XIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {businessProfile.whatsapp_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(businessProfile.whatsapp_url, "_blank")
                      }
                    >
                      <WhatsappIcon size={32} round />
                    </SocialLink>
                  ) : null}
                  {businessProfile.instagram_url ? (
                    <SocialLink
                      onClick={() =>
                        window.open(businessProfile.instagram_url, "_blank")
                      }
                    >
                      <InstagramImg src="/images/ui/instagram.png" />
                    </SocialLink>
                  ) : null}
                </SocialRow>
              </List>
            </SectionCard>

            <SectionCard>
              <SectionHeader>
                <SectionTitle>Business Activities</SectionTitle>
              </SectionHeader>
              <List>
                {businessProfile.primary_activity ? (
                  <ActivityBlock>
                    <ListLabel>Primary</ListLabel>
                    <ItemTitle>
                      {valueToLabel(
                        "business",
                        businessProfile.primary_activity,
                        master
                      )}
                    </ItemTitle>
                  </ActivityBlock>
                ) : null}
                {otherActivities.length > 0 ? (
                  <ActivityBlock>
                    <ListLabel>Other</ListLabel>
                    <PillRow>
                      {otherActivities.map((activity, idx) => (
                        <ActivityPill key={`act-${idx}`}>
                          {activity}
                        </ActivityPill>
                      ))}
                    </PillRow>
                  </ActivityBlock>
                ) : null}
                {!businessProfile.primary_activity &&
                otherActivities.length === 0 ? (
                  <Empty>No business activities added.</Empty>
                ) : null}
              </List>
            </SectionCard>
          </CardGrid>
        </Container>
      ) : (
        <LoadingWrap>
          <Center>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </Center>
        </LoadingWrap>
      )}
    </Page>
  );
};

const Page = styled.div`
  width: 100vw;
  min-height: 100vh;
  background-color: ${colors.lightBackground.hex};
  padding: 8rem 2rem 6rem;
  position: relative;

  @media (max-width: 36em) {
    padding: 7rem 1.2rem 5rem;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 120rem;
  margin: 0 auto;
  width: 100%;
`;

const SectionCard = styled(Card)`
  padding: 2rem 2.4rem;
  border: 1.5px solid ${colors.primary.withOpacity(0.3).hex};
  width: 100%;

  @media (max-width: 36em) {
    padding: 1.6rem 1.6rem;
  }
`;

const HeaderCard = styled(SectionCard)`
  background: linear-gradient(
    135deg,
    ${colors.primary.withOpacity(0.06).hex} 0%,
    ${colors.background.hex} 60%
  );
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  width: 100%;
  flex-wrap: wrap;
`;

const Logo = styled.div`
  width: 11rem;
  height: 11rem;
  border-radius: 1rem;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-color: ${colors.background.hex};
  border: 1.5px solid ${colors.primary.withOpacity(0.2).hex};
  flex-shrink: 0;

  @media (max-width: 36em) {
    width: 8rem;
    height: 8rem;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
  min-width: 18rem;
`;

const Name = styled.h1`
  font-size: 2.8rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
  line-height: 1.2;

  @media (max-width: 36em) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.span`
  font-size: 1.4rem;
  color: ${colors.text.withOpacity(0.65).hex};
  line-height: 1.4;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const ActionButton = styled(Pressable)<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  height: 3.6rem;
  padding: 0 1.4rem;
  border-radius: 0.8rem;
  font-size: 1.3rem;
  font-weight: 600;
  border: 1.5px solid
    ${({ $primary }) =>
      $primary ? colors.primary.hex : colors.primary.withOpacity(0.3).hex};
  background: ${({ $primary }) =>
    $primary ? colors.primary.hex : "transparent"};
  color: ${({ $primary }) =>
    $primary ? colors.white.hex : colors.text.hex};
  transition: all 0.15s;

  &:hover {
    background: ${({ $primary }) =>
      $primary
        ? colors.primary.withOpacity(0.9).hex
        : colors.primary.withOpacity(0.08).hex};
  }
`;

const About = styled.p`
  margin: 1.6rem 0 0;
  font-size: 1.4rem;
  line-height: 1.6;
  color: ${colors.text.withOpacity(0.85).hex};
  white-space: pre-wrap;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  width: 100%;

  @media (max-width: 50em) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1.6rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${colors.grey.hex};
`;

const SectionTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  margin: 0;
  color: ${colors.text.hex};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const ListRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconCircle = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background: ${colors.primary.withOpacity(0.1).hex};
  color: ${colors.primary.hex};
  flex-shrink: 0;
`;

const ListText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`;

const ListLabel = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.text.withOpacity(0.55).hex};
`;

const ItemTitle = styled.span`
  font-size: 1.4rem;
  font-weight: 500;
  color: ${colors.text.hex};
  line-height: 1.3;
`;

const LinkText = styled.span`
  font-size: 1.4rem;
  color: ${colors.primary.hex};
  cursor: pointer;
  word-break: break-all;
  line-height: 1.3;

  &:hover {
    text-decoration: underline;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-top: 0.4rem;
`;

const SocialLink = styled(Pressable)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  overflow: hidden;
  transition: transform 0.15s;

  &:hover {
    transform: translateY(-2px);
  }

  & > * {
    border-radius: 50%;
  }
`;

const InstagramImg = styled.img`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
`;

const ActivityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const ActivityPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 1.25rem;
  font-weight: 500;
  color: ${colors.primary.hex};
  background: ${colors.primary.withOpacity(0.1).hex};
  border: 1px solid ${colors.primary.withOpacity(0.25).hex};
  border-radius: 999px;
  line-height: 1.2;
`;

const Empty = styled.span`
  font-size: 1.3rem;
  color: ${colors.text.withOpacity(0.5).hex};
`;

const LoadingWrap = styled.div`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
